var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { AppError, Row, Col, Space, Card, Text, Button } from '@mc/ui-v4';
import { WaitForQueryData, actionApiCall } from '@mc/react-app-v4';
import { useAppConfig } from '@mc/react-app-base';
import { useAuth } from '@mc/react-auth';
import { ZboApi } from '../api';
import { ZBO_PRIPAD_QUERY_KEY } from '../const/query';
import { PripadNavigation } from '../components/PripadNavigation';
import { PripadHeader } from '../components/PripadHeader';
import { MapWithEtapy } from '../components/MapWithEtapy';
import { DokumentyCard } from '../components/DokumentyCard';
import { SubjektyCard } from '../components/SubjektyCard';
import { buildActiveInfoList, findActiveInfo } from '../helpers/activeInfoHelpers';
export const ZBOPage = () => {
    const [activePripadId, setActivePripadId] = useState(null);
    const [activeTab, setActiveTab] = useState(-1);
    const { pripadId: pathPripadId, zadostId: pathZadostId, zboPripadId: pathZboPripadId, } = useParams();
    const auth = useAuth();
    const { applicationCode } = useAppConfig();
    const queryPripadId = useMemo(() => new URLSearchParams(globalThis.location.search).get('pripadId') || undefined, []);
    const queryZadostId = useMemo(() => new URLSearchParams(globalThis.location.search).get('zadostId') || undefined, []);
    const queryZboPripadId = useMemo(() => new URLSearchParams(globalThis.location.search).get('zboPripadId') || undefined, []);
    const extPripadId = pathPripadId || queryPripadId;
    const zadostIdStr = pathZadostId || queryZadostId;
    const zadostId = zadostIdStr ? Number.parseInt(zadostIdStr, 10) : undefined;
    const zboPripadIdStr = pathZboPripadId || queryZboPripadId;
    const zboPripadId = zboPripadIdStr ? Number.parseInt(zboPripadIdStr, 10) : undefined;
    const isZadostMode = !!zadostId && !extPripadId;
    const isZboPripadMode = !!zboPripadId && !extPripadId && !zadostId;
    // Popis panelu (titulek okna) - prefix je kód hostující aplikace, dle obsahu žádost vs. případ v ZBO
    // Pro aplikaci AG (case insensitive) popis panelu neupravujeme.
    useEffect(() => {
        const prefix = applicationCode && applicationCode !== 'NONE' ? applicationCode.toUpperCase() : '';
        if (prefix === 'AG') {
            return;
        }
        const nazev = isZadostMode ? 'žádost v ZBO' : 'případ v ZBO';
        document.title = prefix ? `${prefix} - ${nazev}` : nazev;
    }, [applicationCode, isZadostMode]);
    const pripadQuery = useQuery({
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c;
            if (isZadostMode) {
                return (_a = (yield ZboApi.getZadostById(zadostId))) === null || _a === void 0 ? void 0 : _a.data;
            }
            if (isZboPripadMode) {
                return (_b = (yield ZboApi.getZadostByZboPripad(zboPripadId))) === null || _b === void 0 ? void 0 : _b.data;
            }
            return (_c = (yield ZboApi.getZadostByPripad(extPripadId))) === null || _c === void 0 ? void 0 : _c.data;
        }),
        queryKey: [ZBO_PRIPAD_QUERY_KEY, isZadostMode ? zadostId : isZboPripadMode ? zboPripadId : extPripadId],
        enabled: !!(extPripadId || zadostId || zboPripadId),
    });
    // Inicializace aktivního případu
    useEffect(() => {
        var _a, _b;
        if (((_b = (_a = pripadQuery.data) === null || _a === void 0 ? void 0 : _a.pripad) === null || _b === void 0 ? void 0 : _b.id) && activePripadId === null) {
            setActivePripadId(pripadQuery.data.pripad.id);
        }
    }, [pripadQuery.data]);
    // Výpočet seznamu aktivních informací
    const activeInfoList = useMemo(() => {
        return buildActiveInfoList(pripadQuery.data);
    }, [pripadQuery.data]);
    // Nalezení aktuálně aktivní informace
    const activeInfo = useMemo(() => {
        return findActiveInfo(activeInfoList, activePripadId);
    }, [activeInfoList, activePripadId]);
    // Případ existuje v AG, ale zatím nemá vazbu na ZBO (server vrací prázdný objekt) -> nabídneme odeslání
    const isPripadNeodeslan = !!extPripadId && !!pripadQuery.data && !pripadQuery.data.pripad;
    const onOdeslatDoZbo = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!extPripadId)
            return;
        yield actionApiCall(() => __awaiter(void 0, void 0, void 0, function* () {
            yield ZboApi.odeslatDoZbo(extPripadId);
            pripadQuery.refetch();
        }));
    }), [extPripadId, pripadQuery]);
    return (React.createElement(WaitForQueryData, { queries: [pripadQuery] }, () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return pripadQuery.isError ? (React.createElement(AppError, { message: (_a = pripadQuery.error) === null || _a === void 0 ? void 0 : _a.messageTitle })) : isPripadNeodeslan ? (React.createElement(Row, { className: 'mx-0 p-1 w-100 justify-content-center', style: { backgroundColor: '#e3e5f2' } },
            React.createElement(Col, { xs: 'auto', className: 'mt-4' },
                React.createElement(Card, { mode: 'view', className: 'p-3', style: { border: '1px solid #d8e4f5' } },
                    React.createElement("div", { className: 'd-flex flex-column align-items-center', style: { gap: 12 } },
                        React.createElement(Text, { style: { fontSize: 14, fontWeight: 600 } },
                            "Vazba na ZBO nebyla pro tento p\u0159\u00EDpad ",
                            extPripadId,
                            " nalezena."),
                        React.createElement(Button, { style: 'primary', onClick: onOdeslatDoZbo }, "Odeslat do ZBO")))))) : (React.createElement(Row, { style: { gap: 7, backgroundColor: '#e3e5f2' }, className: 'mx-0 p-1 w-100' },
            React.createElement(Col, { className: 'p-0', style: { maxWidth: 320 } },
                React.createElement(PripadNavigation, { mainPripad: (_b = pripadQuery.data) === null || _b === void 0 ? void 0 : _b.pripad, dilciZadosti: (_c = pripadQuery.data) === null || _c === void 0 ? void 0 : _c.dilciZadosti, activePripadId: activePripadId, onSelectPripad: setActivePripadId })),
            React.createElement(Col, { className: 'm-0 p-0' },
                React.createElement(Space, { className: 'd-flex flex-column h-100', size: '1', vertical: true },
                    React.createElement(PripadHeader, { activeInfo: activeInfo, queryDataId: (_e = (_d = pripadQuery.data) === null || _d === void 0 ? void 0 : _d.id) === null || _e === void 0 ? void 0 : _e.toString(), onRefresh: () => {
                            pripadQuery.refetch();
                        } }),
                    (activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad) ? (React.createElement(MapWithEtapy, { activeInfo: activeInfo, activeTab: activeTab, onTabChange: setActiveTab, auth: auth })) : null)),
            (activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad) ? (React.createElement(Col, { className: 'p-0', style: { maxWidth: 320 } },
                React.createElement(Space, { size: '1', vertical: true },
                    React.createElement(DokumentyCard, { extPripadId: (_g = (_f = activeInfo.pripad) === null || _f === void 0 ? void 0 : _f.id) === null || _g === void 0 ? void 0 : _g.toString(), zadostId: isZadostMode && typeof activeInfo.id === 'number' ? ((_j = (_h = pripadQuery.data) === null || _h === void 0 ? void 0 : _h.id) !== null && _j !== void 0 ? _j : zadostId) : undefined, dokumenty: activeInfo.pripad.dokumenty }),
                    React.createElement(SubjektyCard, { subjekty: activeInfo.pripad.subjekty, zadostTyp: activeInfo.typ || activeInfo.pripad.typ })))) : null));
    }));
};
//# sourceMappingURL=ZboPage.js.map